import { useContext, useEffect, useRef, useState, memo, FC } from 'react'
import useMeasure from 'react-use-measure'
import { useSpring, a } from '@react-spring/web'
import clsx from 'clsx'
import { INavItem } from '@mkmp/core'
import { Icon } from 'mk-pc'
import { AsideMenuContext } from './context'
import './SubMenu.scss'

interface Props {
  item: INavItem
}

function usePrevious<T>(value: T) {
  const ref = useRef<T>()
  useEffect(() => void (ref.current = value), [value])
  return ref.current
}

interface SubMenuItemProps {
  item: INavItem
  classNames?: string
}

/**
 * 菜单子项
 */
export const SubMenuItem = memo((props: SubMenuItemProps) => {
  const { item, classNames } = props
  const context = useContext(AsideMenuContext)

  const classes = clsx('aside-submenu__subitem', classNames, {
    'is-active': item.id === context.active
  })

  const handleItemClick = () => {
    if (context.onSelect) {
      context.onSelect(item)
    }
  }

  return (
    <li className={classes} onClick={handleItemClick}>
      {item.icon && <Icon name={item.icon} size="16" />}
      <p className="name">{item.name}</p>
    </li>
  )
})

/**
 * 侧边子菜单
 */
export const SubMenu: FC<Props> = memo((props) => {
  const { item } = props
  const context = useContext(AsideMenuContext)
  const [isOpen, setOpen] = useState(item.id === context.active)
  const previous = usePrevious(isOpen)
  const [ref, { height: viewHeight }] = useMeasure()

  const classes = clsx('aside-submenu', {
    'is-expand': item.id === context.expand
  })

  useEffect(() => {
    setOpen(item.id === context.expand)
  }, [context.active])

  const { height, opacity } = useSpring({
    from: { height: 0, opacity: 0 },
    to: {
      height: isOpen ? viewHeight : 0,
      opacity: isOpen ? 1 : 0
    }
  })

  /**
   * 选择导航
   */
  const handleSubMenuClick = () => {
    setOpen(!isOpen)
  }

  return (
    <li className={classes}>
      <div className="aside-submenu__item" onClick={handleSubMenuClick}>
        {item.icon && <Icon name={item.icon} size="20" />}
        <p className="name">{item.name}</p>
      </div>
      <a.div
        style={{
          opacity,
          height: isOpen && previous === isOpen ? 'auto' : height,
          overflow: 'hidden'
        }}
      >
        <ul className="aside-submenu__children" ref={ref}>
          {item.children!.map((nc: INavItem) => (
            <SubMenuItem key={nc.id} item={nc} />
          ))}
        </ul>
      </a.div>
    </li>
  )
})
